import { useState, useRef, useCallback, useEffect } from 'react';

interface UseImageZoomProps {
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
  initialZoom?: number;
}

export const useImageZoom = ({
  minZoom = 1,
  maxZoom = 5,
  zoomStep = 0.3,
  initialZoom = 1,
}: UseImageZoomProps = {}) => {
  const [zoom, setZoom] = useState(initialZoom);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const lastTouchDistance = useRef<number | null>(null);

  const resetZoom = useCallback(() => {
    setZoom(initialZoom);
    setPosition({ x: 0, y: 0 });
  }, [initialZoom]);

  const zoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + zoomStep, maxZoom));
  }, [maxZoom, zoomStep]);

  const zoomOut = useCallback(() => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - zoomStep, minZoom);
      if (newZoom === minZoom) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, [minZoom, zoomStep]);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
      setZoom((prev) => {
        const newZoom = Math.max(minZoom, Math.min(maxZoom, prev + delta));
        if (newZoom === minZoom) {
          setPosition({ x: 0, y: 0 });
        }
        return newZoom;
      });
    },
    [minZoom, maxZoom, zoomStep]
  );

  const handleDoubleClick = useCallback(() => {
    if (zoom > minZoom) {
      resetZoom();
    } else {
      setZoom(2);
    }
  }, [zoom, minZoom, resetZoom]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom > minZoom) {
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      }
    },
    [zoom, minZoom, position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging && zoom > minZoom) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, zoom, minZoom, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const getTouchDistance = useCallback((touches: React.TouchList) => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        lastTouchDistance.current = getTouchDistance(e.touches);
      } else if (e.touches.length === 1 && zoom > minZoom) {
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y,
        });
      }
    },
    [zoom, minZoom, position, getTouchDistance]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && lastTouchDistance.current) {
        e.preventDefault();
        const currentDistance = getTouchDistance(e.touches);
        const delta = (currentDistance - lastTouchDistance.current) * 0.01;

        setZoom((prev) => {
          const newZoom = Math.max(minZoom, Math.min(maxZoom, prev + delta));
          if (newZoom === minZoom) {
            setPosition({ x: 0, y: 0 });
          }
          return newZoom;
        });

        lastTouchDistance.current = currentDistance;
      } else if (e.touches.length === 1 && isDragging && zoom > minZoom) {
        setPosition({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y,
        });
      }
    },
    [isDragging, zoom, minZoom, maxZoom, dragStart, getTouchDistance]
  );

  const handleTouchEnd = useCallback(() => {
    lastTouchDistance.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleWheel, handleMouseMove, handleMouseUp]);

  return {
    zoom,
    position,
    isDragging,
    containerRef,
    imageRef,
    zoomIn,
    zoomOut,
    resetZoom,
    handleDoubleClick,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
